import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { supabase } from "../lib/supabase";
import AppLogo from "../components/AppLogo";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const G = {
  ink:        "#0f1117",
  inkSoft:    "#4a5268",
  green:      "#1e9254",
  greenLight: "#2ab76b",
  greenDark:  "#155c36",
  greenTint:  "#eef8f3",
  deep1:      "#1e4a2e",
  deep2:      "#163322",
  deep3:      "#0d2e1a",
  darkBorder: "#1e4a2e",
  darkBorder2:"#244f32",
  onDarkSub:  "rgba(255,255,255,0.62)",
  onDarkFaint:"rgba(255,255,255,0.34)",
  lightBg:    "#F7F8FA",
  serif:      "'Instrument Serif', Georgia, serif",
  sans:       "'DM Sans', -apple-system, sans-serif",
  tx:         "all 0.2s cubic-bezier(0.4,0,0.2,1)",
};

const IMG_BASE = "https://lrqqjlvoxfanijtdpifr.supabase.co/storage/v1/object/public/pro-landing";
const img = (path) => `${IMG_BASE}/${path}`;
const CITIES = ["Nairobi", "Mombasa", "Kisumu", "Nakuru", "Eldoret", "Thika", "Nyeri", "Kisii", "Meru"];

/* Trade options for the signup form. Kept as its own list (rather
   than derived from FOOTER_COLUMNS below) so the form never breaks
   if the footer copy changes later. Values are written straight to
   pro_signups.trade — keep this in sync with that column if the
   taxonomy changes. */
const TRADES = [
  "Cleaning & Laundry", "Errands & Delivery", "Plumbing", "Electrical",
  "TV & Electronics Repair", "Hair & Beauty", "Home Tutoring", "Photography",
  "Gardening", "Carpentry & Furniture", "Moving & Relocation",
  "Babysitting & Childcare", "Fitness & Training", "Auto Repair", "Jirani Chef",
];

if (typeof document !== "undefined" && !document.getElementById("__jpl_fonts")) {
  const l = document.createElement("link");
  l.id = "__jpl_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

function AmbientGlow({ variant = "top-left" }) {
  const presets = { "top-left": { top: -100, left: -100, size: 420, opacity: 0.16 }, "bottom-right": { bottom: -80, right: -80, size: 340, opacity: 0.09 } };
  const p = presets[variant];
  return <div style={{ position: "absolute", top: p.top, left: p.left, right: p.right, bottom: p.bottom, width: p.size, height: p.size, borderRadius: "50%", background: `radial-gradient(circle, rgba(30,146,84,${p.opacity}) 0%, transparent 70%)`, pointerEvents: "none" }} />;
}

function ProImage({ src, alt, ratio = "4 / 3", tone = "deep2", fill = false, fit = "cover" }) {
  const [errored, setErrored] = useState(false);
  const toneMap = { deep1: G.deep1, deep2: G.deep2, deep3: G.deep3, green: G.green, tint: G.greenTint, light: "#e7eaee" };
  return (
    <div style={{ position: "relative", width: "100%", ...(fill ? { height: "100%" } : { aspectRatio: ratio }), overflow: "hidden", background: toneMap[tone] || G.deep2 }}>
      {!errored && src && <img src={src} alt={alt} onError={() => setErrored(true)} style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }} />}
    </div>
  );
}

function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}
function Reveal({ children, delay = 0, style = {} }) {
  const [ref, vis] = useReveal();
  return <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(16px)", transition: `opacity 0.5s ease ${delay}s, transform 0.5s ease ${delay}s`, ...style }}>{children}</div>;
}

/* ═══ TYPE SYSTEM — Instrument Serif regular is the ONLY heading
   voice on this page, at every level: section headlines AND
   card/item titles. DM Sans bold is reserved for body copy,
   labels, and nav/link text. CTA buttons (Register, Register Now,
   Start Earning, View more) now use Instrument Serif too, at
   regular weight — the font only ships weight 400/italic, so we
   don't force a 700 that the browser would have to fake-bold. ═══ */
function Headline({ children, onDark = true, size = 48, align = "left", heavy = false, style = {} }) {
  return <h2 style={{ fontFamily: G.serif, fontWeight: heavy ? 700 : 400, fontSize: size, color: onDark ? "#fff" : G.ink, margin: 0, lineHeight: 1.12, letterSpacing: "-0.01em", textAlign: align, ...style }}>{children}</h2>;
}
function SubHeading({ children, onDark = true, size = 21, style = {} }) {
  return <h4 style={{ fontFamily: G.serif, fontWeight: 400, fontSize: size, color: onDark ? "#fff" : G.ink, margin: 0, lineHeight: 1.25, ...style }}>{children}</h4>;
}
function Eyebrow({ children, onDark = true, align = "left" }) {
  return <p style={{ fontSize: 15, fontWeight: 600, color: onDark ? "#8fe3ac" : G.greenDark, margin: "0 0 14px", fontFamily: G.sans, textAlign: align }}>{children}</p>;
}

/* ═══ ARROW SCROLLER — snap-scroll with edge fade masks (so the
   trailing card fades rather than hard-cuts) + arrow controls. ═══ */
function ArrowScroller({ children, isMobile, onDark = false }) {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const update = () => {
    const el = ref.current; if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };
  useEffect(() => { update(); }, []);
  const scroll = (dir) => { ref.current?.scrollBy({ left: dir * 380, behavior: "smooth" }); setTimeout(update, 320); };
  const mask = canLeft && canRight
    ? "linear-gradient(to right, transparent 0, #000 40px, #000 calc(100% - 60px), transparent 100%)"
    : canRight ? "linear-gradient(to left, transparent 0, #000 60px)"
    : canLeft ? "linear-gradient(to right, transparent 0, #000 40px)" : "none";
  const iconColor = onDark ? "#fff" : G.ink;
  const borderColor = onDark ? G.darkBorder : "#d8dce2";
  return (
    <div>
      <div style={{ maskImage: mask, WebkitMaskImage: mask }}>
        <div ref={ref} onScroll={update} style={{ display: "flex", gap: 22, overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none", paddingBottom: 8 }} className="jpl-hide-scrollbar">
          {children}
        </div>
      </div>
      {!isMobile && (
        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={() => scroll(-1)} disabled={!canLeft} style={{ width: 42, height: 42, borderRadius: "50%", border: `1.5px solid ${borderColor}`, background: onDark ? "transparent" : "#fff", color: iconColor, cursor: canLeft ? "pointer" : "default", opacity: canLeft ? 1 : 0.3, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button onClick={() => scroll(1)} disabled={!canRight} style={{ width: 42, height: 42, borderRadius: "50%", border: `1.5px solid ${borderColor}`, background: onDark ? "transparent" : "#fff", color: iconColor, cursor: canRight ? "pointer" : "default", opacity: canRight ? 1 : 0.3, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══ CATEGORY ICONS — one distinct premium line icon per trade ═══ */
const CAT_ICON_PATHS = {
  cleaning: <><path d="M3 7l7-4 7 4M6 8v9a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8" /><path d="M9 13h6" /></>,
  plumbing: <path d="M14.7 6.3a4 4 0 0 0-5.6 4.6L3 17l4 4 6.1-6.1a4 4 0 0 0 4.6-5.6l-2.8 2.8-2.2-2.2 2.8-2.8z" />,
  electrical: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  electronics: <><rect x="2" y="4" width="20" height="14" rx="2" /><path d="M8 21h8M12 18v3" /></>,
  beauty: <><path d="M6 3l6 6-9 9v3h3l9-9M13 6l5-3 3 3-3 5" /></>,
  tutoring: <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>,
  photography: <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>,
  gardening: <path d="M12 22c0-6 4-8 4-13a4 4 0 0 0-8 0c0 5 4 7 4 13z" />,
  carpentry: <><path d="M14.5 3.5 21 10l-3 3-6.5-6.5z" /><path d="M3 21l6-6M9 15l3 3" /></>,
  moving: <><rect x="1" y="7" width="15" height="12" rx="1" /><path d="M16 11h4l3 3v5h-7z" /><circle cx="5.5" cy="19.5" r="1.5" /><circle cx="18.5" cy="19.5" r="1.5" /></>,
  childcare: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />,
  fitness: <><path d="M6.5 6.5 17.5 17.5" /><path d="M4 4l3 3M20 20l-3-3M9 4l-2-2M4 9l-2-2M15 20l2 2M20 15l2 2" /></>,
  auto: <><path d="M5 17h14M6 17V9l2-4h8l2 4v8" /><circle cx="7.5" cy="17" r="1.5" /><circle cx="16.5" cy="17" r="1.5" /></>,
  errands: <><circle cx="6" cy="17" r="3.5" /><circle cx="18" cy="17" r="3.5" /><path d="M9.5 17H14l-2-8H8l2.5 4H14l3-6.5" /></>,
  chef: <path d="M12 2a4 4 0 0 0-3.9 5C6.4 7.6 5 9.5 5 11.5c0 1.4.7 2.6 1.8 3.3L6 21h12l-.8-6.2c1.1-.7 1.8-1.9 1.8-3.3 0-2-1.4-3.9-3.1-4.5A4 4 0 0 0 12 2z" />,
};
const CategoryIcon = ({ name, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{CAT_ICON_PATHS[name]}</svg>
);

const ALL_TRADES = [
  { icon: "cleaning", title: "Become a Cleaning Pro", body: "Home and office cleaning jobs near you.", path: "/services/cleaning" },
  { icon: "errands", title: "Become a Courier", body: "Deliver errands and packages, get paid per job.", path: "/services/errands-delivery" },
  { icon: "plumbing", title: "Become a Plumber", body: "Pipe repairs, installs, and emergency call-outs.", path: "/services/plumbing" },
  { icon: "electrical", title: "Become an Electrician", body: "Wiring, installs, and repair requests nearby.", path: "/services/electrical" },
  { icon: "electronics", title: "Fix Electronics & Earn", body: "TV, appliance, and phone repair requests.", path: "/services/electronics-repair" },
  { icon: "beauty", title: "Become a Beauty Pro", body: "Hair, braiding, and beauty bookings near you.", path: "/services/hair-beauty" },
  { icon: "tutoring", title: "Become a Tutor", body: "Home tutoring jobs across every subject.", path: "/services/home-tutoring" },
  { icon: "photography", title: "Shoot & Get Booked", body: "Event, portrait, and product photography work.", path: "/services/photography" },
  { icon: "gardening", title: "Become a Gardener", body: "Lawn care and landscaping jobs nearby.", path: "/services/gardening" },
  { icon: "carpentry", title: "Become a Carpenter", body: "Furniture and carpentry work, big or small.", path: "/services/carpentry-furniture" },
  { icon: "moving", title: "Help People Move", body: "Moving and relocation jobs across your city.", path: "/services/moving" },
  { icon: "childcare", title: "Become a Nanny", body: "Babysitting and childcare requests near you.", path: "/services/childcare" },
  { icon: "fitness", title: "Train Clients & Earn", body: "Personal training and fitness coaching jobs.", path: "/services/fitness" },
  { icon: "auto", title: "Become a Mechanic", body: "Auto repair and servicing jobs nearby.", path: "/services/auto-repair" },
  { icon: "chef", title: "Become a Home Chef", body: "Private cooking and catering requests.", path: "/services/chef" },
];

/* ═══ MEGA MENU — tabbed left side (Company / Support / Cities /
   Program), full scrollable right panel listing every trade with
   a premium icon, matching Bolt's density. ═══ */
const LINK_PATHS = {
  "Cleaning & Laundry": "/services/cleaning", "Errands & Delivery": "/services/errands-delivery",
  "Plumbing": "/services/plumbing", "Electrical": "/services/electrical",
  "TV & Electronics Repair": "/services/electronics-repair", "Hair & Beauty": "/services/hair-beauty",
  "Home Tutoring": "/services/home-tutoring", "Photography": "/services/photography",
  "Gardening": "/services/gardening", "Carpentry & Furniture": "/services/carpentry-furniture",
  "Moving & Relocation": "/services/moving", "Babysitting & Childcare": "/services/childcare",
  "Fitness & Training": "/services/fitness", "Auto Repair": "/services/auto-repair", "Jirani Chef": "/services/chef",
  "Become a JiraniPro": "/pro-signup", "Community Hub": "/community-hub", "Tips & Resources": "/tips",
  "Events": "/events", "Guides": "/guides",
  "About JiraniApp": "/about", "Careers": "/careers", "Trust & Safety": "/trust-and-safety", "Press & News": "/news",
  "Help & Support": "/help", "Contact Sales": "/contact-sales", "Terms of Service": "/terms", "Privacy Policy": "/privacy",
};

/* ═══ MEGA MENU — Cities dropped per your call (no real pages
   built for them yet); every remaining link actually navigates. ═══ */
const MEGA_COLUMNS = {
  Program: ["Become a JiraniPro", "Community Hub", "Tips & Resources", "Events", "Guides"],
  Company: ["About JiraniApp", "Careers", "Trust & Safety", "Press & News"],
  Support: ["Help & Support", "Contact Sales", "Terms of Service", "Privacy Policy"],
};
function MegaMenu({ open, onClose }) {
  const navigate = useNavigate();
  const go = (label) => { onClose(); navigate(LINK_PATHS[label] || "/"); };
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: G.deep3, overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "20px 28px" }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#fff" }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px 60px", display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 60, minHeight: "70vh" }} className="jpl-mega-grid">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "40px 32px" }} className="jpl-mega-cols">
          {Object.entries(MEGA_COLUMNS).map(([heading, links]) => (
            <div key={heading}>
              <SubHeading size={19} style={{ marginBottom: 18 }}>{heading}</SubHeading>
              {links.map(label => <p key={label} onClick={() => go(label)} style={{ fontSize: 15, color: G.onDarkSub, margin: "0 0 12px", cursor: "pointer" }}>{label}</p>)}
            </div>
          ))}
        </div>
        <div style={{ background: G.deep2, borderRadius: 16, padding: 10, maxHeight: "72vh", overflowY: "auto" }}>
          {ALL_TRADES.map(c => (
            <div key={c.title} onClick={() => { onClose(); navigate(c.path); }} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: 14, borderRadius: 12, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: G.green, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CategoryIcon name={c.icon} />
              </div>
              <div>
                <SubHeading size={16} style={{ marginBottom: 4 }}>{c.title}</SubHeading>
                <p style={{ fontSize: 13, color: G.onDarkSub, margin: 0 }}>{c.body}</p>
              </div>
              <svg style={{ marginLeft: "auto", flexShrink: 0, marginTop: 8 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={G.onDarkFaint} strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══ HEADER — transparent overlay on the hero photo. Secondary
   sticky bar appears on scroll: label and links grouped LEFT,
   CTA pushed right. "Register" now scrolls back up to the hero
   signup form (via onRegisterClick, passed down from the page)
   instead of routing to /pro-onboarding — that flow isn't live
   yet, the form on this page is the only registration path. ═══ */
function Header({ isMobile, onRegisterClick }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSubNav, setShowSubNav] = useState(false);
  useEffect(() => {
    const h = () => setShowSubNav(window.scrollY > (isMobile ? 460 : 560));
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, [isMobile]);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <header style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1320, margin: "0 auto", padding: isMobile ? "18px 20px" : "24px 40px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ transform: isMobile ? "scale(0.72)" : "none", transformOrigin: "left center" }}>
            <span style={{ display: "inline-block" }}><AppLogo theme="onDark" /></span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 14 : 24 }}>
            {!isMobile && (
              <>
                <button onClick={() => navigate("/about")} style={{ background: "none", border: "none", fontSize: 14.5, fontWeight: 500, color: "#fff", cursor: "pointer", fontFamily: G.sans }}>About</button>
                <button onClick={() => navigate("/help")} style={{ background: "none", border: "none", fontSize: 14.5, fontWeight: 500, color: "#fff", cursor: "pointer", fontFamily: G.sans }}>Help & Support</button>
                <button onClick={onRegisterClick} style={{ background: "#fff", color: G.ink, border: "none", borderRadius: 999, padding: "9px 22px", fontSize: 15, fontWeight: 400, cursor: "pointer", fontFamily: G.serif }}>Register</button>
              </>
            )}
            <button onClick={() => setMenuOpen(true)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fff", padding: 0 }}>
              <svg width={isMobile ? 24 : 22} height={isMobile ? 24 : 22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
            </button>
          </div>
        </div>
      </header>

      <div style={{ position: "sticky", top: 0, zIndex: 90, background: "#FCFCFB", borderBottom: "1px solid #ECEEF1", transform: showSubNav ? "translateY(0)" : "translateY(-100%)", transition: "transform 0.25s ease", height: showSubNav ? "auto" : 0, overflow: "hidden" }}>
        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 28px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: G.serif, fontSize: 18, color: G.ink }}>JiraniPro Program</span>
          <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
            {!isMobile && (
              <nav style={{ display: "flex", gap: 24 }}>
                {[["Overview", "how"], ["Earnings", "earnings"], ["Levels", "levels"]].map(([label, id]) => (
                  <button key={id} onClick={() => scrollTo(id)} style={{ background: "none", border: "none", fontSize: 13.5, fontWeight: 500, color: G.inkSoft, cursor: "pointer", fontFamily: G.sans }}>{label}</button>
                ))}
              </nav>
            )}
            <button onClick={onRegisterClick} style={{ background: G.green, color: "#fff", border: "none", borderRadius: 999, padding: "8px 20px", fontSize: 14, fontWeight: 400, cursor: "pointer", fontFamily: G.serif }}>Register</button>
          </div>
        </div>
      </div>

      <MegaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

/* ═══ SEARCH SELECT — generic search+select dropdown, shared by
   the Area and Trade fields in the signup form below. ═══ */
function SearchSelect({ value, onChange, options, placeholder = "Select", icon }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = options.filter(o => o.toLowerCase().includes(query.toLowerCase()));
  return (
    <div style={{ position: "relative" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "13px 14px", borderRadius: 6, border: "1.5px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.06)", cursor: "pointer" }}>
        {icon}
        <span style={{ fontSize: 15, color: value ? "#fff" : "rgba(255,255,255,0.42)", fontFamily: G.sans, flex: 1 }}>{value || placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: G.deep2, border: `1px solid ${G.darkBorder2}`, borderRadius: 8, padding: 6, zIndex: 10, maxHeight: 220, overflowY: "auto" }}>
          <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search" style={{ width: "100%", padding: "8px 10px", border: "none", background: "rgba(255,255,255,0.06)", borderRadius: 6, color: "#fff", fontSize: 13, marginBottom: 6, outline: "none", boxSizing: "border-box" }} />
          {filtered.length === 0 && <div style={{ padding: "8px 10px", fontSize: 13, color: "rgba(255,255,255,0.4)" }}>No matches</div>}
          {filtered.map(o => (
            <div key={o} onClick={() => { onChange(o); setOpen(false); setQuery(""); }} style={{ padding: "8px 10px", borderRadius: 6, fontSize: 13.5, color: "#fff", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>{o}</div>
          ))}
        </div>
      )}
    </div>
  );
}

const AREA_ICON = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>;
const TRADE_ICON = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>;

/* ═══ SIGNUP FORM — writes straight to pro_signups (name, phone,
   area, trade; status defaults to 'pending' in the DB). This is
   the only registration path live right now: no redirect to
   /pro-onboarding, since that flow isn't built yet. On success we
   swap the form for an inline confirmation and leave the lead for
   the admin Signups tab to pick up. ═══ */
function SignupForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", area: "Nairobi", trade: "" });
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");
  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.trade) {
      setErrMsg("Please select your trade.");
      setState("error");
      return;
    }
    setState("loading"); setErrMsg("");
    try {
      const { error } = await supabase.from("pro_signups").insert([{
        name: form.name.trim(),
        phone: `+254${form.phone.trim()}`,
        area: form.area,
        trade: form.trade,
      }]);
      if (error) throw error;
      setState("success");
    } catch (err) {
    console.error("Pro signup insert failed:", err);
    setErrMsg("Something went wrong. Please try again or WhatsApp us directly.");
    setState("error");
   }
  };

  if (state === "success") {
    return (
      <div style={{ textAlign: "center", padding: "18px 6px 8px" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: G.green, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <SubHeading style={{ marginBottom: 10 }}>You're on the list</SubHeading>
        <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.72)", lineHeight: 1.65, margin: 0 }}>
          Thanks{form.name.trim() ? `, ${form.name.trim().split(" ")[0]}` : ""}. Our team will call you within 48 hours to verify your details and get you live.
        </p>
      </div>
    );
  }

  const labelStyle = { display: "block", fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.6)", marginBottom: 7, fontFamily: G.sans };
  const inputStyle = { width: "100%", padding: "13px 14px", borderRadius: 6, border: "1.5px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.06)", fontFamily: G.sans, fontSize: 15, color: "#fff", outline: "none", boxSizing: "border-box" };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 14 }}><label style={labelStyle}>Full name</label><input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Grace Wanjiru" required style={inputStyle} /></div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Phone number</label>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 12px", borderRadius: 6, border: "1.5px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.06)", flexShrink: 0 }}>
            <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iOTAwIiBoZWlnaHQ9IjYwMCIgdmlld0JveD0iLTEyMCAtODAgMjQwIDE2MCIgeG1sbnM6dj0iaHR0cHM6Ly92ZWN0YS5pby9uYW5vIj48ZGVmcz48cGF0aCBpZD0iQSIgc3Ryb2tlLW1pdGVybGltaXQ9IjEwIiBkPSJNLTEgNTUuNDI2aDJWLTM4YzItMiAyLTUgMi04IDAtMiAwLTEwLTMtMTguNjYzQy0zLTU2LTMtNDgtMy00NmMwIDMgMCA2IDIgOHoiIHRyYW5zZm9ybT0icm90YXRlKDMwKSIvPjwvZGVmcz48cGF0aCBmaWxsPSIjZmZmIiBkPSJNLTEyMC04MGgyNDBWODBoLTI0MHoiLz48cGF0aCBkPSJNLTEyMC04MGgyNDB2NDhoLTI0MHoiLz48cGF0aCBmaWxsPSIjMDYwIiBkPSJNLTEyMCAzMmgyNDB2NDhoLTI0MHoiLz48ZyBpZD0iQiI+PHVzZSB4bGluazpocmVmPSIjQSIgc3Ryb2tlPSIjMDAwIi8+PHVzZSB4bGluazpocmVmPSIjQSIgZmlsbD0iI2ZmZiIvPjwvZz48dXNlIHhsaW5rOmhyZWY9IiNCIiB0cmFuc2Zvcm09InNjYWxlKC0xIDEpIi8+PHBhdGggZmlsbD0iI2IwMCIgZD0iTS0xMjAtMjR2NDhoMTAxYzMgOCAxMyAyNCAxOSAyNHMxNi0xNiAxOS0yNGgxMDF2LTQ4SDE5QzE2LTMyIDYtNDggMC00OHMtMTYgMTYtMTkgMjR6Ii8+PHBhdGggaWQ9IkMiIGQ9Ik0xOSAyNGMzLTggNS0xNiA1LTI0cy0yLTE2LTUtMjRjLTMgOC01IDE2LTUgMjRzMiAxNiA1IDI0Ii8+PHVzZSB4bGluazpocmVmPSIjQyIgdHJhbnNmb3JtPSJzY2FsZSgtMSAxKSIvPjxnIGZpbGw9IiNmZmYiPjxlbGxpcHNlIHJ4PSI0IiByeT0iNiIvPjxwYXRoIGlkPSJEIiBkPSJNMSA1Ljg1czQgOCA0IDIxLTQgMjEtNCAyMXoiLz48dXNlIHhsaW5rOmhyZWY9IiNEIiB0cmFuc2Zvcm09InNjYWxlKC0xKSIvPjx1c2UgeGxpbms6aHJlZj0iI0QiIHRyYW5zZm9ybT0ic2NhbGUoLTEgMSkiLz48dXNlIHhsaW5rOmhyZWY9IiNEIiB0cmFuc2Zvcm09InNjYWxlKDEgLTEpIi8+PC9nPjwvc3ZnPg==" alt="Kenya" style={{ width: 20, height: 14, borderRadius: 2, objectFit: "cover", flexShrink: 0 }} />
            <span style={{ color: "#fff", fontSize: 15 }}>+254</span>
          </div>
          <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="7XX XXX XXX" required style={{ ...inputStyle, flex: 1 }} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}><label style={labelStyle}>Area</label><SearchSelect value={form.area} onChange={v => setForm(f => ({ ...f, area: v }))} options={CITIES} icon={AREA_ICON} /></div>
      <div style={{ marginBottom: 18 }}><label style={labelStyle}>Trade</label><SearchSelect value={form.trade} onChange={v => setForm(f => ({ ...f, trade: v }))} options={TRADES} placeholder="Select your trade" icon={TRADE_ICON} /></div>
      {errMsg && <p style={{ fontSize: 13, color: "#ffb4b4", background: "rgba(255,0,0,0.08)", border: "1px solid rgba(255,0,0,0.2)", borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>{errMsg}</p>}
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.6, margin: "0 0 14px" }}>By signing up you confirm you have read and understand our <span onClick={() => navigate("/privacy")} style={{ color: G.greenLight, fontWeight: 600, cursor: "pointer" }}>Privacy terms</span>.</p>
      <button type="submit" disabled={state === "loading"} style={{ width: "100%", height: 46, border: "none", borderRadius: 6, background: state === "loading" ? G.greenDark : G.green, color: "#fff", fontSize: 16, fontWeight: 400, fontFamily: G.serif, cursor: state === "loading" ? "default" : "pointer" }}>{state === "loading" ? "Saving your spot..." : "Register Now"}</button>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 14 }}>Run a company that places workers? <span onClick={() => navigate("/business")} style={{ color: G.greenLight, fontWeight: 600, cursor: "pointer" }}>Explore JiraniApp for Business.</span></p>
    </form>
  );
}

function Hero({ isMobile }) {
  return (
    <section style={{ position: "relative", minHeight: isMobile ? 620 : 680, background: G.deep3, overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0 }}><ProImage src={img("testimonials/testimonial-jemo.jpg")} alt="A JiraniPro at work" tone="deep3" fill /></div>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(13,46,26,0.4) 0%, rgba(13,46,26,0.1) 30%, rgba(13,46,26,0.98) 100%)" }} />
      <div style={{ position: "relative", zIndex: 2, maxWidth: 1240, margin: "0 auto", padding: isMobile ? "84px 20px 32px" : "130px 40px 64px", minHeight: isMobile ? 560 : 680, boxSizing: "border-box", display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: isMobile ? "flex-start" : "space-between", alignItems: isMobile ? "stretch" : "flex-end", gap: isMobile ? 28 : 36 }}>
        <div style={{ maxWidth: 580 }}>
          <h1 style={{ fontFamily: G.serif, fontWeight: 700, color: "#fff", fontSize: isMobile ? 36 : 60, lineHeight: 1.08, letterSpacing: "-0.01em", margin: "0 0 20px" }}>
            Become a <em style={{ fontStyle: "italic", color: "#8fe3ac" }}>JiraniPro</em> and start earning this week
          </h1>
          <p style={{ fontSize: isMobile ? 15.5 : 18, color: "rgba(255,255,255,0.88)", lineHeight: 1.6, margin: 0, fontFamily: G.sans, maxWidth: 460 }}>Choose your own hours, get matched with clients nearby, and get paid the moment the job is done.</p>
        </div>
        <div style={{ background: "rgba(13,46,26,0.7)", backdropFilter: "blur(14px)", border: `1px solid ${G.darkBorder2}`, borderRadius: 10, padding: "28px 26px", width: isMobile ? "100%" : 400, flexShrink: 0, boxSizing: "border-box" }}>
          <SubHeading style={{ fontSize: 21, marginBottom: 20 }}>Start earning with JiraniApp</SubHeading>
          <SignupForm />
        </div>
      </div>
    </section>
  );
}

function TrustBand({ isMobile }) {
  return (
    <section style={{ background: G.deep3, padding: isMobile ? "60px 24px" : "96px 40px", position: "relative", overflow: "hidden" }}>
      <AmbientGlow variant="top-left" />
      <div style={{ maxWidth: 800, margin: "0 auto", position: "relative" }}>
        <Headline align="center" size={isMobile ? 27 : 44}>Trusted by JiraniPros across Nairobi, Mombasa, Kisumu &amp; Nakuru</Headline>
        <p style={{ fontSize: 17, color: G.onDarkSub, lineHeight: 1.7, margin: "22px auto 0", maxWidth: 540, fontFamily: G.sans, textAlign: "center" }}>JiraniApp connects verified professionals to thousands of clients looking for trusted help nearby, every single day.</p>
      </div>
    </section>
  );
}

/* ═══ FEATURE GRID — light T.bg, icons restored, titles serif ═══ */
const FeatIcon = ({ name, size = 27 }) => {
  const p = {
    eye: <><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>,
    cal: <><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
    trend: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>,
    gift: <><rect x="3" y="8" width="18" height="4" /><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" /><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5" /></>,
    chat: <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={G.ink} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{p[name]}</svg>;
};
function FeatureGrid({ isMobile }) {
  const items = [
    { icon: "eye", title: "Fair earnings, fast payouts", body: "See exactly what a job pays before you accept it, and get paid instantly on M-Pesa the moment it's done." },
    { icon: "cal", title: "Flexible schedule", body: "Work your own hours across any of your trades. No shifts, no fixed schedule, no minimum hours." },
    { icon: "trend", title: "Earn more as you level up", body: "Move from Beginner to Business Pro and unlock priority placement, credit access, and bigger contracts." },
    { icon: "gift", title: "Keep every tip", body: "Clients can tip you directly inside the app. It goes straight to you, no cut taken." },
    { icon: "chat", title: "Real support, not a chatbot", body: "If something goes wrong on a job, our team is a phone call away, not a support ticket." },
  ];
  return (
    <section style={{ background: G.lightBg, padding: isMobile ? "60px 24px" : "100px 40px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Reveal style={{ marginBottom: 56 }}>
          <Headline onDark={false} size={isMobile ? 28 : 46} style={{ maxWidth: 640, marginBottom: 20 }}>Freedom to earn your way</Headline>
          <p style={{ fontSize: 17, color: G.inkSoft, lineHeight: 1.7, margin: 0, maxWidth: 680, fontFamily: G.sans }}>Whether you work full-time or a few hours a week, JiraniApp gives you the flexibility to work on your terms, with quick access to earnings and opportunities to earn more when demand is high.</p>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", columnGap: 48, rowGap: 44 }}>
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.03}>
              <FeatIcon name={it.icon} />
              <SubHeading onDark={false} size={19} style={{ margin: "18px 0 10px" }}>{it.title}</SubHeading>
              <p style={{ fontSize: 15, color: G.inkSoft, lineHeight: 1.65, margin: 0 }}>{it.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══ TESTIMONIALS — equal-height cards via a fixed-height text
   panel, centered heading with hero-style italic accent. ═══ */
function TestimonialCard({ src, alt, name, trade, location, quote }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ borderRadius: 16, overflow: "hidden", border: `1.5px solid ${hov ? G.greenLight : G.darkBorder}`, transform: hov ? "translateY(-6px)" : "translateY(0)", boxShadow: hov ? "0 20px 40px -12px rgba(0,0,0,0.4)" : "none", transition: G.tx, background: G.deep2, display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ aspectRatio: "4 / 3" }}><ProImage src={src} alt={alt} tone="deep3" fill /></div>
      <div style={{ padding: "26px 26px 30px", minHeight: 260, display: "flex", flexDirection: "column" }}>
        <SubHeading size={19} style={{ marginBottom: 4 }}>{name}</SubHeading>
        <p style={{ fontSize: 13, color: G.onDarkSub, margin: "0 0 16px" }}>{trade} · {location}</p>
        <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.9)", lineHeight: 1.75, margin: 0 }}>{quote}</p>
      </div>
    </div>
  );
}
function Testimonials({ isMobile }) {
  return (
    <section style={{ background: G.deep3, padding: isMobile ? "60px 24px" : "100px 40px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Reveal style={{ marginBottom: 48, textAlign: "center" }}>
          <Headline align="center" size={isMobile ? 27 : 42}>Real stories from real <em style={{ fontStyle: "italic", color: "#8fe3ac" }}>JiraniPros</em></Headline>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 24, alignItems: "stretch" }}>
          <Reveal><TestimonialCard src={img("testimonials/testimonial-brian.jpg")} alt="Brian Otieno" name="Brian Otieno" trade="Electrician" location="Westlands, Nairobi"
            quote="Before JiraniApp, I'd go days without a single call. Now my week fills up before Monday's even over, and clients see my rating before they call, so they've basically already decided to book me by the time they reach out. The money's on its way to my M-Pesa the second I mark a job done." /></Reveal>
          <Reveal delay={0.05}><TestimonialCard src={img("testimonials/testimonial-grace.jpg")} alt="Grace Wanjiru" name="Grace Wanjiru" trade="Hair & Beauty" location="Bamburi, Mombasa"
            quote="I used to spend more time chasing clients than working. Now my reviews do that job for me, people book me because of what past clients said, not because they know someone who knows me." /></Reveal>
        </div>
      </div>
    </section>
  );
}

/* ═══ EARNINGS — light T.bg, big colored circles, illustrations
   restored, big scrollable cards, corrected content, big photo. ═══ */
function EarningsStrip({ isMobile }) {
  const items = [
    { n: "01", title: "You set your price", body: "You decide what a job is worth. JiraniApp doesn't set your rates, you do.", tone: G.deep3, src: img("earnings/earnings-01.jpg") },
    { n: "02", title: "Charge more for distance & time", body: "It's your call to add more for jobs that are further away or take longer than usual.", tone: G.deep1, src: img("earnings/earnings-02.jpg") },
    { n: "03", title: "Charge more for rush jobs", body: "Add a premium for urgent or same-day requests. The client decides if it's worth it to them.", tone: G.green, src: img("earnings/earnings-03.jpg") },
    { n: "04", title: "Withdraw any time", body: "Earnings reflect on your earnings page in real time. Withdraw to M-Pesa whenever you want, no fixed schedule.", tone: G.deep2, src: img("earnings/earnings-04.jpg") },
    { n: "05", title: "Business Pro salary", body: "Booked by a business to join their team? Business Pros earn a guaranteed monthly salary on top of job earnings.", tone: G.greenLight, src: img("earnings/earnings-05.jpg") },
    { n: "06", title: "Top performer rewards", body: "The best-rated, most active JiraniPros each month earn extra rewards on top of their regular earnings.", tone: G.deep1, src: img("earnings/earnings-06.jpg") },
  ];
  return (
    <section id="earnings" style={{ background: G.lightBg, padding: isMobile ? "60px 0" : "100px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <Reveal style={{ marginBottom: 56, textAlign: "center" }}>
          <Eyebrow onDark={false} align="center">Earnings</Eyebrow>
          <Headline onDark={false} align="center" size={isMobile ? 28 : 48}>How your earnings are calculated</Headline>
          <p style={{ fontSize: 17, color: G.inkSoft, lineHeight: 1.7, margin: "22px auto 0", maxWidth: 640, fontFamily: G.sans, textAlign: "center" }}>Earnings are always shown before you accept a job. Every job includes a guaranteed base, plus fair additions for distance, time, and peak-hour rewards.</p>
        </Reveal>
        <ArrowScroller isMobile={isMobile}>
          {items.map(it => (
            <div key={it.n} style={{ scrollSnapAlign: "start", flex: "0 0 340px", background: "#fff", border: "1px solid #e4e7ef", borderRadius: 18, display: "flex", flexDirection: "column", minHeight: 460 }}>
              <div style={{ padding: "32px 28px 0" }}>
                <div style={{ width: 58, height: 58, borderRadius: "50%", background: it.tone, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24 }}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 17, fontFamily: G.sans }}>{it.n}</span>
                </div>
                <SubHeading onDark={false} size={20} style={{ marginBottom: 12 }}>{it.title}</SubHeading>
                <p style={{ fontSize: 14.5, color: G.inkSoft, lineHeight: 1.65, margin: 0 }}>{it.body}</p>
              </div>
              <div style={{ flex: 1, marginTop: 24 }}><ProImage src={it.src} alt={it.title} tone="tint" fill /></div>
            </div>
          ))}
        </ArrowScroller>
      </div>
      <Reveal delay={0.08} style={{ marginTop: 60 }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: isMobile ? "0 24px" : 0 }}><ProImage src={img("bigbands/levels-band.jpg")} alt="A JiraniPro at work" tone="light" ratio={isMobile ? "4 / 3" : "21 / 8"} /></div>
      </Reveal>
    </section>
  );
}

/* ═══ REQUIREMENTS — image wrapper uses padding + fit="contain"
   so the icon shrinks to fit inside its fixed-size card instead of
   being cropped/zoomed to fill it; the padding blends into the
   card's own background color since both share the same tone, so
   there's no visible seam between image and card. Card height stays
   fixed at 380 on every breakpoint — only the image shrinks. ═══ */
function Requirements({ isMobile }) {
  const items = [
        { title: "A valid ID", body: "To confirm who you are before clients ever meet you.", tone: "tint", src: img("requirements/requirement-valid-id.jpg") },
    { title: "A smartphone", body: "Android or iOS, with a local Kenyan number.", tone: "green", src: img("requirements/requirement-smartphone.jpg") },
    { title: "Your trade tools", body: "Whatever your work already needs, nothing extra.", tone: "deep3", src: img("requirements/requirement-trade-tools.jpg") },
    { title: "An M-Pesa number", body: "So every payout has somewhere instant to land.", tone: "tint", src: img("requirements/requirement-mpesa-number.jpg") },
  ];
  const dark = (t) => t === "green" || t === "deep3";
  const [pressed, setPressed] = useState(null);
  return (
    <section style={{ background: G.lightBg, padding: isMobile ? "60px 0" : "100px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <Reveal style={{ marginBottom: 56, textAlign: "center" }}>
          <Eyebrow onDark={false} align="center">Requirements</Eyebrow>
          <Headline onDark={false} align="center" size={isMobile ? 28 : 48}>What you need to get started</Headline>
          <p style={{ fontSize: 17, color: G.inkSoft, lineHeight: 1.7, margin: "22px auto 0", maxWidth: 620, fontFamily: G.sans, textAlign: "center" }}>Registration is simple. It only takes a few minutes, and you'll be guided through verification every step of the way.</p>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 22 }}>
          {items.map((it, i) => (
            <Reveal key={it.title}>
              <div onMouseDown={() => setPressed(i)} onMouseUp={() => setPressed(null)} onMouseLeave={() => setPressed(null)} style={{ borderRadius: 20, overflow: "hidden", height: 380, display: "flex", flexDirection: "column", background: it.tone === "green" ? G.green : it.tone === "deep3" ? G.deep3 : G.greenTint, border: it.tone === "deep3" ? `1px solid ${G.darkBorder}` : "none", cursor: "pointer", transform: pressed === i ? "scale(0.98)" : "scale(1)", boxShadow: pressed === i ? "0 4px 12px rgba(0,0,0,0.15)" : "0 10px 30px -10px rgba(0,0,0,0.15)", transition: G.tx }}>
                <div style={{ padding: "36px 36px 0" }}>
                  <SubHeading onDark={dark(it.tone)} size={24} style={{ marginBottom: 12 }}>{it.title}</SubHeading>
                  <p style={{ fontSize: 15.5, color: dark(it.tone) ? "rgba(255,255,255,0.78)" : G.inkSoft, lineHeight: 1.6, margin: 0, maxWidth: 280 }}>{it.body}</p>
                </div>
                <div style={{ flex: 1, marginTop: 22, minHeight: 0, padding: 10, boxSizing: "border-box" }}>
                <div style={{ width: "100%", height: "100%", maskImage: "radial-gradient(ellipse at center, black 60%, transparent 92%)", WebkitMaskImage: "radial-gradient(ellipse at center, black 60%, transparent 92%)" }}>
                <ProImage src={it.src} alt={it.title} tone={it.tone === "tint" ? "tint" : it.tone} fill fit="contain" />
                </div>
               </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══ REGISTRATION STEPS — desktop keeps the 4-column grid; mobile
   renders the same cards inside the ArrowScroller so they sit in
   one horizontal scrollable row instead of wrapping into a 2x2
   grid. Cards use minHeight instead of a fixed height so the text
   never gets clipped, and a gradient overlay at the base of each
   photo fades into the card's own background color (computed via
   cardBg) so there's no hard seam between image and text. ═══ */
function StepsStrip({ isMobile }) {
  const navigate = useNavigate();
  const steps = [
    { n: "01", title: "Register", body: "Enter your name, area, and phone number to create your account. Takes just a few minutes.", tone: "deep3", src: img("steps/step-1.jpg")},
    { n: "02", title: "Get verified", body: "Our team calls you personally to confirm your ID and trade credentials.", tone: "deep1", src: img("steps/step-2.jpg")},
    { n: "03", title: "Go live", body: "Your profile appears in search with a verified badge, ready to be booked.", tone: "green", src: img("steps/step-3.jpg")},
    { n: "04", title: "Start earning", body: "Accept your first request and get paid the same day, straight to M-Pesa.", tone: "tint", src: img("steps/step-4.jpg") },
  ];
  const dark = (t) => t !== "tint";
  const cardBg = (t) => t === "green" ? G.green : t === "tint" ? G.greenTint : t === "deep1" ? G.deep1 : G.deep3;

  const renderCard = (s) => {
    const bg = cardBg(s.tone);
    return (
      <div key={s.n} style={{ borderRadius: 16, overflow: "hidden", minHeight: isMobile ? 380 : 460, display: "flex", flexDirection: "column", background: bg, border: s.tone === "deep3" ? `1px solid ${G.darkBorder}` : "none", ...(isMobile ? { flex: "0 0 240px", scrollSnapAlign: "start" } : {}) }}>
        <div style={{ height: isMobile ? 170 : 280, minHeight: 0, flexShrink: 0, position: "relative" }}>
          <ProImage src={s.src} alt={s.title} tone={s.tone} fill />
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 48, background: `linear-gradient(to bottom, rgba(0,0,0,0), ${bg})`, pointerEvents: "none" }} />
        </div>
        <div style={{ padding: isMobile ? "18px 16px" : "26px 24px 30px", flex: 1, display: "flex", flexDirection: "column" }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: dark(s.tone) ? "rgba(255,255,255,0.55)" : "#5c7a68", letterSpacing: "0.04em", margin: "0 0 12px", fontFamily: G.sans }}>STEP {s.n}</p>
          <SubHeading onDark={dark(s.tone)} size={isMobile ? 16 : 20} style={{ marginBottom: 10 }}>{s.title}</SubHeading>
          <p style={{ fontSize: isMobile ? 12.5 : 14.5, color: dark(s.tone) ? "rgba(255,255,255,0.72)" : G.inkSoft, lineHeight: 1.55, margin: 0 }}>{s.body}</p>
        </div>
      </div>
    );
  };

  return (
    <section id="how" style={{ background: G.deep3, padding: isMobile ? "60px 0" : "100px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <Reveal style={{ marginBottom: 56, textAlign: "center" }}>
          <Eyebrow align="center">Registration process</Eyebrow>
          <Headline align="center" size={isMobile ? 28 : 48}>Four easy steps to start earning</Headline>
          <p style={{ fontSize: 17, color: G.onDarkSub, lineHeight: 1.7, margin: "22px auto 0", maxWidth: 620, fontFamily: G.sans, textAlign: "center" }}>You'll be ready to earn within days, with clear guidance and a simple process from start to finish.</p>
        </Reveal>
        {isMobile ? (
          <ArrowScroller isMobile={isMobile} onDark>
            {steps.map(renderCard)}
          </ArrowScroller>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 }}>
            {steps.map(s => <Reveal key={s.n}>{renderCard(s)}</Reveal>)}
          </div>
        )}
      </div>
    </section>
  );
}

/* ═══ LEVELS — light T.bg, centered heading, scroller w/ mask
   fade (fixes the Business Pro hard-cut edge). Bottom padding now
   matches the gap used below the safety image (see SafetyBlock)
   instead of the old, much larger 100px/60px value. ═══ */
function Levels({ isMobile }) {
  const levels = [
    { index: 1, name: "Beginner", unlock: "Unlocked on verification. No waiting period.", benefits: ["Full profile live in search", "Every incoming client request nearby", "Verified badge from day one", "Instant M-Pesa payment on every job"] },
    { index: 2, name: "Junior Pro", unlock: "Unlocked after a consistent run of 5-star jobs.", benefits: ["Priority placement above Beginner", "Faster payout processing", "Repeat-client requests", "Access to credit for tools or transport"] },
    { index: 3, name: "Senior Pro", unlock: "Unlocked with a proven track record at scale.", benefits: ["Top-of-search placement", "Featured in JiraniApp's marketing", "First access to long-term contracts", "A dedicated support line"] },
    { index: 4, name: "Business Pro", unlock: "For pros ready to run a team, not just a job.", benefits: ["KES 7,500 per business that books you", "Guaranteed monthly salary when placed with a team", "SHA health cover, guaranteed", "Dedicated account manager"] },
  ];
    return (
    <section style={{ background: G.lightBg, padding: isMobile ? "60px 0" : "100px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <Reveal style={{ marginBottom: 56, textAlign: "center" }}>
          <Eyebrow onDark={false} align="center">Where you go from here</Eyebrow>
          <Headline onDark={false} align="center" size={isMobile ? 28 : 48}>Each JiraniPro level</Headline>
        </Reveal>
        <ArrowScroller isMobile={isMobile}>
          {levels.map(lvl => (
            <div key={lvl.index} style={{ scrollSnapAlign: "start", flex: "0 0 320px", background: lvl.index === 4 ? G.green : "#fff", border: lvl.index === 4 ? "none" : "1px solid #e4e7ef", borderRadius: 18, padding: "30px 26px" }}>
              <span style={{ fontFamily: G.sans, fontWeight: 700, fontSize: 12.5, color: lvl.index === 4 ? "rgba(255,255,255,0.7)" : G.greenDark, letterSpacing: "0.04em" }}>LEVEL {String(lvl.index).padStart(2, "0")}</span>
              <SubHeading onDark={lvl.index === 4} size={25} style={{ margin: "10px 0 12px" }}>{lvl.name}</SubHeading>
              <p style={{ fontSize: 14, color: lvl.index === 4 ? "rgba(255,255,255,0.8)" : G.inkSoft, marginBottom: 18 }}>{lvl.unlock}</p>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {lvl.benefits.map(b => <li key={b} style={{ fontSize: 13.5, color: lvl.index === 4 ? "rgba(255,255,255,0.85)" : G.inkSoft, paddingLeft: 16, position: "relative", lineHeight: 1.6, marginBottom: 8 }}><span style={{ position: "absolute", left: 0, top: 7, width: 5, height: 5, borderRadius: "50%", background: lvl.index === 4 ? "#fff" : G.green }} />{b}</li>)}
              </ul>
            </div>
          ))}
        </ArrowScroller>
      </div>
    </section>
  );
}

/* ═══ SAFETY BLOCK — the band image now uses fit="cover" so it
   fills the full width instead of letterboxing with empty side
   bars under fit="contain". paddingTop above the image is reduced
   from 140/90 to match `gap` (the same spacing already used below
   the image), and Levels' bottom padding above was reduced to
   match too, so both gaps around this image are now equal. ═══ */
function SafetyBlock({ isMobile, onCta }) {
  const gap = isMobile ? 64 : 96;
  const topGap = isMobile ? 24 : 32;
  return (
    <section style={{ background: G.lightBg, paddingTop: topGap, paddingBottom: gap }}>
      <Reveal style={{ marginBottom: gap }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: isMobile ? "0 24px" : 0 }}><ProImage src={img("bigbands/safety-band.jpg")} alt="A JiraniPro at work" tone="light" ratio={isMobile ? "4 / 3" : "21 / 8"} fit="cover" /></div>
      </Reveal>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
        <Reveal>
          <Headline onDark={false} align="center" size={isMobile ? 27 : 40}>Working safely, on your terms</Headline>
          <p style={{ fontSize: 16, color: G.inkSoft, lineHeight: 1.75, margin: "24px 0 14px" }}>Every JiraniPro is ID-verified before going live, so clients can book someone they've never met with confidence. There's no pressure to rush a job to hit a target.</p>
          <p style={{ fontSize: 16, color: G.inkSoft, lineHeight: 1.75, margin: "0 0 34px" }}>If something goes wrong on a job, our support team is a phone call away, and every job you complete builds a track record that opens up credit and higher-value work.</p>
          <button onClick={onCta} style={{ background: G.green, color: "#fff", border: "none", borderRadius: 6, padding: "15px 34px", fontSize: 17, fontWeight: 400, fontFamily: G.serif, cursor: "pointer" }}>Start Earning</button>
        </Reveal>
      </div>
    </section>
  );
}

function FaqItemDark({ q, a, isOpen, onToggle }) {
  const bodyRef = useRef(null);
  return (
    <div style={{ background: G.deep2, borderRadius: 8, border: `1.5px solid ${isOpen ? G.greenLight : "transparent"}`, marginBottom: 12, overflow: "hidden" }}>
      <button onClick={onToggle} style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 16 }}>
        <span style={{ fontSize: 15.5, fontWeight: 600, color: "#fff", fontFamily: G.sans }}>{q}</span>
        <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#fff" }}>{isOpen ? "–" : "+"}</span>
      </button>
      <div ref={bodyRef} style={{ overflow: "hidden", maxHeight: isOpen ? (bodyRef.current?.scrollHeight + "px") : "0px", transition: "max-height 0.28s cubic-bezier(0.4,0,0.2,1)" }}>
        <p style={{ fontSize: 14.5, color: G.onDarkSub, lineHeight: 1.7, padding: "0 22px 20px", margin: 0 }}>{a}</p>
      </div>
    </div>
  );
}
function FaqDark({ isMobile }) {
  const [openIdx, setOpenIdx] = useState(-1);
  const [expanded, setExpanded] = useState(false);
  const faqs = [
    { q: "Does it cost anything to join?", a: "Nothing at all. Creating and maintaining your JiraniApp profile is completely free, and that will never change. You only pay a small platform fee after you've already been paid for a completed job." },
    { q: "How exactly does the M-Pesa payment work?", a: "When a client books you and marks the job complete, payment is processed instantly to your registered M-Pesa number." },
    { q: "What does 'verified' actually mean?", a: "We verify your national ID, phone number, and trade credentials before your profile goes live." },
    { q: "How does the credit / loan access work?", a: "From Junior Pro upward, you can access short-term loans for tools, stock, or transport, based on your job history." },
    { q: "I don't have a smartphone or reliable data. Can I still work?", a: "Yes. JiraniApp also works over WhatsApp and basic phone calls." },
    { q: "What happens after I sign up today?", a: "Our team calls you personally, usually within 48 hours, to verify your details." },
    { q: "How does the Business Pro salary work?", a: "Business Pros booked by a business to join their team earn a guaranteed monthly salary, in addition to the per-business payout." },
    { q: "Is JiraniApp a real, registered company?", a: "Yes. JiraniApp is a registered Kenyan business, currently completing trademark registration with KIPI." },
  ];
  const visible = expanded ? faqs : faqs.slice(0, 4);
  return (
    <section id="faq" style={{ background: G.deep3, padding: isMobile ? "60px 0" : "100px 0" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 24px" }}>
        <Reveal style={{ textAlign: "center", marginBottom: 44 }}>
          <Headline align="center" size={isMobile ? 26 : 38}>Frequently asked questions</Headline>
          <p style={{ fontSize: 15, color: G.onDarkSub, margin: "16px 0 0" }}>Still need help? Our support team is here to help you get started.</p>
        </Reveal>
        <Reveal delay={0.05}>
          {visible.map((f, i) => <FaqItemDark key={f.q} q={f.q} a={f.a} isOpen={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />)}
          {!expanded && faqs.length > 4 && <div style={{ textAlign: "center", marginTop: 24 }}><button onClick={() => setExpanded(true)} style={{ background: G.green, color: "#fff", border: "none", borderRadius: 6, padding: "12px 26px", fontSize: 15, fontWeight: 400, fontFamily: G.serif, cursor: "pointer" }}>View More</button></div>}
        </Reveal>
      </div>
    </section>
  );
}

const FOOTER_COLUMNS = [
  { heading: "Categories", links: ["Cleaning & Laundry", "Errands & Delivery", "Plumbing", "Electrical", "TV & Electronics Repair", "Hair & Beauty", "Home Tutoring", "Photography", "Gardening", "Carpentry & Furniture", "Moving & Relocation", "Babysitting & Childcare", "Fitness & Training", "Auto Repair", "Jirani Chef"] },
  { heading: "For Professionals", links: ["Become a JiraniPro", "Community Hub", "Tips & Resources", "Events", "Guides"] },
  { heading: "Company", links: ["About JiraniApp", "Careers", "Trust & Safety", "Press & News"] },
  { heading: "Support", links: ["Help & Support", "Contact Sales", "Terms of Service", "Privacy Policy"] },
];
/* ═══ FOOTER — same dark canvas as FAQ, separated by a visible line.
   Column headers use the same Instrument Serif brand heading font
   as everything else on the page, sized up to stand out. ═══ */
function MPesaMark({ height = 14 }) {
  return (
    <svg viewBox="0 0 512 273" style={{ height, width: height * (512 / 273), display: "inline-block" }} aria-label="M-Pesa">
      <path d="m361 184c-6.68-1.88-14.8-5.54-19.9-8.94-1.52-1.02-1.18-2.66 1.73-8.42l3.62-7.15 9.09 4.74c15.9 8.33 29 7.71 29-1.4 0-4.39-3.64-6.71-16.4-10.4-17.5-5.12-25.4-13.2-24.1-24.4 1.71-14 12.7-21.4 31.2-21.3 10.9 0.114 26.2 4.19 28.2 7.54 0.754 1.22-4.68 15.5-5.89 15.5-0.323 0-2.95-1.18-5.82-2.62-13.7-6.88-27.1-6.75-27.1 0.259 0 4.67 3.4 7.15 14.8 10.8 14.8 4.79 20 8.11 23.4 15.1 3.36 6.94 2.86 14-1.48 21.2-5.65 9.29-25.2 13.9-40.6 9.62zm-351-38.1v-38.8h21.2l11.3 22.6c6.22 12.4 11.6 22.6 12 22.6s5.77-10.2 12-22.6l11.3-22.6h21.2v77.6h-16.9v-24c0-13.2-0.486-24-1.08-24-0.595 0-5.3 8.88-10.5 19.7l-9.38 19.7h-13.1l-20.3-41.6-0.77 50.1h-16.9zm188-0.203v-38.9l21.7 0.73c18.6 0.626 22.7 1.18 28.2 3.85 9.45 4.57 12.7 9.93 13.3 21.9 0.942 19.9-8.5 28.6-32.1 30l-12.7 0.725v20.7h-18.3zm41.7-0.948c3.02-2.59 3.79-4.5 3.79-9.29 0-9.26-3.54-11.9-16.6-12.6l-10.6-0.533v25.7h9.79c8.21 0 10.4-0.53 13.6-3.27zm31.6 1.15v-38.8h59.2v15.5h-40.9v15.5h38.1v15.5h-38.1v15.5h42.3v15.5h-60.6zm142 30.7c1.93-4.5 9.38-21.9 16.5-38.5l13-30.3 19.7-0.805 15.1 36.4c8.32 20 15.5 37.5 16.1 38.9 0.83 2.31 0.0141 2.5-9.31 2.12l-10.2-0.417-4.68-12.7h-34.8l-4.95 12.7-20 0.828zm49.6-21.9c-6.36-17.3-10.1-26.4-10.8-26.4-0.478 7e-3 -3.09 5.84-5.81 13-2.71 7.12-5.15 13.5-5.4 14.1-0.258 0.625 4.72 1.14 11.1 1.14 8.64 0 11.4-0.443 10.9-1.76z" fill="#39b54a" stroke="#39b54a" strokeWidth="2"/>
      <path d="m172 80.6v9.36c-15.2 0.0524-30.2 0.0158-45.3 0.0158-12.9 0-14.4 6.09-14.4 15.7v131c0 7.81 6.44 14.1 14.4 14.1h43.1c7.99 0 14.4-6.29 14.4-14.1v-157c0-1.97-1.59-5.95-5.39-5.77-4.27 0.237-6.78 3.37-6.91 5.77zm-37.1 27.1h27.6c11.6 0 13.2 7.25 13.2 15.1v46.7c0 5.8-5.03 14.9-13.2 14.9h-27.6c-9.97 0-15.2-8.6-15.2-14.9v-46.7c0-7.81 4.13-15.1 15.2-15.1z" fill="#d8e3d2" fillRule="evenodd" strokeWidth=".998"/>
      <path d="m104 155c9.07-0.942 17.2-5.6 26.4-17.6 10.4 15.8 24.7 15.2 39.2 16.8-15.6 0.163-13.6 5.52-34.8 4.03-6.77-0.473-17.9-0.936-30.7-3.19z" fill="#9d4c44" fillRule="evenodd" strokeWidth=".998"/>
      <path d="m161 122 33.8 16.4c-24.1 26.9-58.5 25.1-90.2 16.8 17.5-0.0728 33 6.4 56.3-33.1z" fill="#ed1c24" fillRule="evenodd" strokeWidth=".998"/>
    </svg>
  );
}

const SOCIALS = [
  { name: "Instagram", url: "https://www.instagram.com/jiraniapp", d: "M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.265.07 1.645.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.265.058-1.645.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608C4.516 2.497 5.783 2.225 7.15 2.233 8.415 2.175 8.795 2.163 12 2.163zm0-2.163C8.741 0 8.332.014 7.052.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.052.014 8.332 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.855.601 3.697 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.332 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 1.855-.085 3.697-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.058-1.28.072-1.689.072-4.948 0-3.259-.014-3.668-.072-4.948-.085-1.855-.601-3.697-1.942-5.038C20.645.673 18.803.157 16.948.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" },
  { name: "TikTok", url: "https://www.tiktok.com/@jiraniapp", d: "M9 3v10.55A4 4 0 1 0 13 17V7h4a4 4 0 0 1-4-4z" },
  { name: "X", url: "https://twitter.com/jiraniapp", d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.261 5.635 5.903-5.635zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
  { name: "Facebook", url: "https://www.facebook.com/jiraniapp", d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
];
   function LandingFooter({ isMobile }) {
  const navigate = useNavigate();
  return (
    <footer style={{ background: G.deep3, fontFamily: G.sans, position: "relative", overflow: "hidden", borderTop: `1px solid ${G.darkBorder}` }}>
      <AmbientGlow variant="top-left" /><AmbientGlow variant="bottom-right" />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "48px 24px 32px" : "72px 40px 40px", position: "relative" }}>
        <div style={{ display: "inline-block", marginBottom: 48 }}><AppLogo theme="onDark" /></div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1.4fr 1fr 1fr 1fr", gap: isMobile ? "28px 20px" : "0 32px", marginBottom: 48 }}>
          {FOOTER_COLUMNS.map(col => (
            <div key={col.heading}>
              <SubHeading size={17} style={{ marginBottom: 18 }}>{col.heading}</SubHeading>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, columns: col.links.length > 8 ? 2 : 1 }}>{col.links.map(l => <li key={l} style={{ marginBottom: 10, breakInside: "avoid" }}><span onClick={() => navigate(LINK_PATHS[l] || "/")} style={{ fontSize: 13.5, color: G.onDarkSub, cursor: "pointer" }}>{l}</span></li>)}</ul>
            </div>
          ))}
        </div>
                <div style={{ paddingTop: 8, display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 24 : 12, justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", borderRadius: 8, padding: "6px 12px" }}>
            <span style={{ fontSize: 11.5, color: G.onDarkFaint, fontWeight: 600 }}>Lipa Na</span>
            <MPesaMark height={16} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            {SOCIALS.map(s => (
              <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" title={s.name} style={{ color: G.onDarkFaint, display: "inline-flex" }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d={s.d} /></svg>
              </a>
            ))}
          </div>
          <div style={{ textAlign: isMobile ? "left" : "right" }}>
            <p style={{ fontSize: 12.5, color: G.onDarkFaint, margin: "0 0 3px" }}>© Jirani Kenya Co. Ltd. 2026</p>
            <p style={{ fontSize: 11, color: G.onDarkFaint, margin: 0, opacity: 0.75 }}>Delta Corner, Chiromo Road, Westlands, Nairobi</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function JiraniProLandingPage() {
  const { isMobile } = useBreakpoint();
  const scrollToSignup = () => window.scrollTo({ top: 0, behavior: "smooth" });
  return (
    <div style={{ fontFamily: G.sans, background: G.deep3, color: "#fff" }}>
      <style>{`
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { transition: none !important; animation: none !important; } }
        .jpl-hide-scrollbar::-webkit-scrollbar { display: none; }
        @media (max-width: 900px) { .jpl-mega-grid { grid-template-columns: 1fr !important; gap: 32px !important; } .jpl-mega-cols { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 600px) { .jpl-mega-cols { grid-template-columns: 1fr !important; } }
      `}</style>
      <Header isMobile={isMobile} onRegisterClick={scrollToSignup} />
      <Hero isMobile={isMobile} />
      <TrustBand isMobile={isMobile} />
      <FeatureGrid isMobile={isMobile} />
      <Testimonials isMobile={isMobile} />
      <EarningsStrip isMobile={isMobile} />
      <Requirements isMobile={isMobile} />
      <StepsStrip isMobile={isMobile} />
      <Levels isMobile={isMobile} />
      <SafetyBlock isMobile={isMobile} onCta={scrollToSignup} />
      <FaqDark isMobile={isMobile} />
      <LandingFooter isMobile={isMobile} />
    </div>
  );
}